'use client';

import React, { useEffect, useState } from 'react';

import { Cms } from '@bytel/product-wall';
import {
    Alignable,
    Box,
    BoxContent,
    Button,
    ButtonList,
    ButtonVariant,
    Justifiable,
    Text,
    TextLevels,
    TextMarkup,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { ProductParentFull } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';

import { TradeInStep } from '@constants/trade-in';

import {
    ReturnSessionTradeInProduct,
    clearSessionTradeInProduct,
    getSessionTradeInStep,
} from '@product-detail/helpers';

export type ProductOffersDiscountsProps = {
    product: ProductParentFull;
    tradeInStep: TradeInStep;
    tradeInBonusPlan?: string;
    openTradeInModal: () => void;
    tradeInBonus: number;
    setTradeInStep: (step: TradeInStep) => void;
};

export function ProductOffersDiscounts({
    product,
    tradeInStep,
    tradeInBonusPlan,
    openTradeInModal,
    tradeInBonus,
    setTradeInStep,
}: ProductOffersDiscountsProps) {
    const [tradeInPhone, setTradeInPhone] = useState<string>('');
    const [tradeInPrice, setTradeInPrice] = useState<number>(0);

    const handleResetTradeIn = () => {
        clearSessionTradeInProduct();
        setTradeInStep(TradeInStep.NOT_STARTED);
    };

    useEffect(() => {
        if (tradeInStep === TradeInStep.CONFIRMED) {
            const product = getSessionTradeInStep(
                TradeInStep.SEARCH,
            ) as ReturnSessionTradeInProduct[TradeInStep.SEARCH];
            const tradeIn = getSessionTradeInStep(
                TradeInStep.CONFIRMATION,
            ) as ReturnSessionTradeInProduct[TradeInStep.CONFIRMATION];
            if (tradeInPhone !== product?.name) {
                setTradeInPhone(product?.name.replace(/ /g, '\u00A0') ?? '');
                setTradeInPrice(tradeIn?.amount ?? 0);
            }
        }
    }, [tradeInStep]);

    const renderContent = () => {
        if (tradeInStep === TradeInStep.CONFIRMED) {
            return (
                <>
                    <Text level={TextLevels.TWO}>
                        <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Voici le montant estimé{' '}
                        </Text>
                        <Text markup={TextMarkup.SPAN}>
                            pour l&apos;achat de ce nouveau téléphone
                            {tradeInBonus > 0 ? ` avec forfait Bouygues Telecom ${tradeInBonusPlan}` : ''},{' '}
                        </Text>
                        <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            si vous vendez votre {tradeInPhone}.
                        </Text>
                    </Text>
                    <Box flat shadowless>
                        {tradeInBonus > 0 ? (
                            <>
                                <BoxContent className='p-small'>
                                    <View flexable justify={Justifiable.SPACE_BETWEEN} align={Alignable.ALIGNED_CENTER}>
                                        <div>
                                            <Text level={TextLevels.TWO} marginless>
                                                Valeur estimée du
                                            </Text>
                                            <Text level={TextLevels.TWO}>{tradeInPhone}&nbsp;:</Text>
                                        </div>
                                        <InlinePrice level={TextLevels.TWO} amount={tradeInPrice} />
                                    </View>
                                </BoxContent>
                                <BoxContent className='p-small'>
                                    <View flexable justify={Justifiable.SPACE_BETWEEN} align={Alignable.ALIGNED_CENTER}>
                                        <Text level={TextLevels.TWO} marginless>
                                            Bonus reprise&nbsp;:
                                        </Text>
                                        <InlinePrice level={TextLevels.TWO} amount={tradeInBonus} />
                                    </View>
                                </BoxContent>
                                <BoxContent backgroundColor={TrilogyColor.MAIN_FADE} className='p-small'>
                                    <View flexable justify={Justifiable.SPACE_BETWEEN} align={Alignable.ALIGNED_CENTER}>
                                        <Text level={TextLevels.TWO} marginless>
                                            Montant total estimé&nbsp;:
                                        </Text>
                                        <InlinePrice
                                            amount={tradeInPrice + tradeInBonus}
                                            className='title is-level-5'
                                        />
                                    </View>
                                </BoxContent>
                            </>
                        ) : (
                            <BoxContent backgroundColor={TrilogyColor.INFO_FADE} className='p-small'>
                                <View flexable justify={Justifiable.SPACE_BETWEEN} align={Alignable.ALIGNED_CENTER}>
                                    <div>
                                        <Text level={TextLevels.TWO} marginless>
                                            Valeur estimée du
                                        </Text>
                                        <Text level={TextLevels.TWO}>{tradeInPhone}&nbsp;:</Text>
                                    </div>
                                    <InlinePrice amount={tradeInPrice} className='title is-level-5' />
                                </View>
                            </BoxContent>
                        )}
                    </Box>
                </>
            );
        } else {
            return (
                <>
                    <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                        Estimez-le au meilleur prix et bénéficiez d&apos;un bonus reprise.
                    </Text>
                    {tradeInBonus > 0 ? (
                        <Text level={TextLevels.TWO}>
                            <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                <span>Jusqu&apos;à&nbsp;</span>
                                <InlinePrice amount={tradeInBonus} level={TextLevels.TWO} />
                                <span>&nbsp;de bonus reprise&nbsp;</span>
                            </Text>
                            <Text markup={TextMarkup.SPAN}>
                                pour l&apos;achat de ce téléphone et la souscription d&apos;un forfait Bouygues Telecom{' '}
                                {tradeInBonusPlan} si vous vendez votre ancien mobile.
                            </Text>
                        </Text>
                    ) : (
                        <Text level={TextLevels.TWO}>
                            Profitez d&apos;une remise différée sur votre nouveau téléphone en faisant reprendre votre
                            ancien mobile. Vous y gagnez, et l&apos;environnement aussi.
                        </Text>
                    )}
                </>
            );
        }
    };

    const renderActionButtons = () => {
        switch (tradeInStep) {
            case TradeInStep.NOT_STARTED:
            case TradeInStep.SEARCH:
                return (
                    <Button variant={ButtonVariant.PRIMARY} onClick={openTradeInModal}>
                        Estimer mon mobile
                    </Button>
                );
            case TradeInStep.CONFIRMED:
                return (
                    <ButtonList>
                        <Button variant={ButtonVariant.SECONDARY} onClick={openTradeInModal}>
                            Modifier ma reprise
                        </Button>
                        <Button variant={ButtonVariant.SECONDARY} onClick={handleResetTradeIn}>
                            Annuler ma reprise
                        </Button>
                    </ButtonList>
                );
            default:
                return (
                    <Button variant={ButtonVariant.PRIMARY} onClick={openTradeInModal}>
                        Finaliser mon estimation
                    </Button>
                );
        }
    };

    return (
        <Box shadowless>
            <BoxContent>
                <Title level={TitleLevels.FOUR}>Vous avez un mobile à revendre&nbsp;?</Title>
                <form id='offersDiscounts'>
                    {renderContent()}
                    {renderActionButtons()}
                    <Cms content={product.specialOffersCms} />
                </form>
            </BoxContent>
        </Box>
    );
}
